import { Command, CommandEmpty, CommandList } from 'components/ui/command';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { useGetArchivedChats, useUnArchiveChat } from 'pages/hooks/use-archive';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, MessageSquare } from 'lucide-react';
import { useChatStore } from '../../state/store/chat/use-chat-store';
import { SidebarItem } from 'components/blocks/layout/types/sidebar-conversation';
import { useTranslation } from 'react-i18next';

export function ArchiveChat({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { isPending: isFetchingArchivedChats, mutateAsync: fetchArchivedChats } =
    useGetArchivedChats();

  const { mutateAsync: unarchiveChat } = useUnArchiveChat();
  const [archivedList, setArchivedList] = useState<SidebarItem[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    fetchArchivedChats({ page: 0, pageSize: 200 })
      .then((data) => {
        setArchivedList(data.results || []);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [isOpen, fetchArchivedChats]);

  if (!isOpen) return null;

  const handleUnarchive = async (chat: SidebarItem) => {
    try {
      await unarchiveChat(chat.ConversationId);
      useChatStore.getInitialState().appendToSidebarConversationsList(chat);
      setArchivedList((prev) => prev.filter((item) => item.ConversationId !== chat.ConversationId));
    } catch (error) {
      console.error(error);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          showOverlay={false}
          className="p-0 gap-0 w-[calc(100%-2rem)] max-w-[800px] sm:w-[calc(100%-3rem)] sm:max-w-[800px] rounded-lg shadow-lg border outline-none mx-auto"
        >
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <DialogTitle className="text-lg font-semibold">{t('ARCHIVED_CHATS')}</DialogTitle>
            </div>
          </div>

          <Command className="rounded-lg h-full flex flex-col">
            <CommandList className="h-[600px] overflow-auto relative">
              {isFetchingArchivedChats ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>{t('LOADING_ARCHIVED_CHATS')}...</span>
                  </div>
                </div>
              ) : archivedList.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <CommandEmpty className="py-8 text-center">
                    {t('NO_ARCHIVED_CONVERSATIONS_FOUND')}
                  </CommandEmpty>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4">{t('CONVERSATION')}</th>
                      <th className="text-right p-4">{t('ACTIONS')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedList.map((chat) => (
                      <tr key={chat.ConversationId} className="border-b hover:bg-accent">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{chat.ConversationTitle}</span>
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => handleUnarchive(chat)}
                            className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground"
                          >
                            {t('UNARCHIVE')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </div>,
    document.body
  );
}
